import { useState, FormEvent, ChangeEvent } from "react";
import { useDispatch } from "react-redux";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { resources } from "@constants/hardcoded-strings";
import { ButtonTypes } from "@enums/button-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { SelectOption } from "@idw-types/common";
import { AxnCompany } from "@idw-types/data/reference-data/axn";
import { WorkflowTemplateDetails } from "@idw-types/data/workflow-template-details";
import { createWorkflowTemplateReduxRequest } from "@redux-requests/workflow-templates";
import { DefaultModal } from "../../common/modals/default-modal/default-modal";
import { InputFormGroup } from "../../common/input-form-group/input-form-group";
import { ButtonWithSpinner } from "../../common/button-with-spinner/button-with-spinner";
import { OrganizationSelectFormGroup } from "../../common/select-form-groups";
import { TextareaFormGroup } from "../../common/textarea-form-group/textarea-form-group";

type AddWorkflowTemplateModalProps = {
    onModalClose: () => void;
    axnCompanies: Array<AxnCompany>;
};

type AddWorkflowTemplateModalState = {
    requestInProgress: boolean;
    workflowTemplate: Partial<WorkflowTemplateDetails>;
    selectedOrganizationOption: SelectOption;
};

const defaultWorkflowTemplate: Partial<WorkflowTemplateDetails> = {
    name: "",
    description: "",
    descriptionURL: "",
    organizationID: null,
    template: ""
};


export const AddWorkflowTemplateModal = (props: AddWorkflowTemplateModalProps) => {
    const dispatch = useDispatch();
    const {
        onModalClose,
        axnCompanies
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const [state, setState] = useState<AddWorkflowTemplateModalState>({
        requestInProgress: false,
        workflowTemplate: defaultWorkflowTemplate,
        selectedOrganizationOption: null
    });

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setState({ ...state, requestInProgress: true });

        const newWorkflowTemplate = await createWorkflowTemplateReduxRequest(dispatch)(
            state.workflowTemplate,
            axnCompanies
        );
        if (newWorkflowTemplate) {
            onModalClose();
        } else {
            setState({ ...state, requestInProgress: false });
        }
    };

    const handleUpdateName = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            workflowTemplate: {
                ...state.workflowTemplate,
                name: event.target.value
            }
        });
    };

    const handleUpdateDescription = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            workflowTemplate: {
                ...state.workflowTemplate,
                description: event.target.value
            }
        });
    };

    const handleUpdateDescriptionURL = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            workflowTemplate: {
                ...state.workflowTemplate,
                descriptionURL: event.target.value
            }
        });
    };

    const handleUpdateOrganizationOption = (newValue: SelectOption) => {
        setState({
            ...state,
            workflowTemplate: {
                ...state.workflowTemplate,
                organizationID: newValue?.value || null
            },
            selectedOrganizationOption: newValue
        });
    };

    const handleUpdateTemplate = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            workflowTemplate: {
                ...state.workflowTemplate,
                template: event.target.value
            }
        });
    };

    const resource = resources.modals.addWorkflowTemplate;

    return (
        <DefaultModal
            automationId="add-workflow-template-modal"
            ariaLabelledBy="add-workflow-template-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={false}
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header id={"add-workflow-template-title"} closeButton>
                    <Modal.Title>{resource.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <InputFormGroup
                        controlId="workflow-template-name-input"
                        label={resource.inputs.templateName.label}
                        ariaLabel={resource.inputs.templateName.label}
                        automationId="workflow-template-name-input"
                        placeholder={resource.inputs.templateName.placeholder}
                        value={state.workflowTemplate.name}
                        onChange={handleUpdateName}
                        disabled={state.requestInProgress}
                        isRequired={true}
                        labelWidth={labelWidth}
                    />
                    <InputFormGroup
                        controlId="workflow-template-description-input"
                        label={resource.inputs.description.label}
                        ariaLabel={resource.inputs.description.label}
                        automationId="workflow-template-description-input"
                        placeholder={resource.inputs.description.placeholder}
                        value={state.workflowTemplate.description}
                        onChange={handleUpdateDescription}
                        disabled={state.requestInProgress}
                        isRequired={true}
                        labelWidth={labelWidth}
                    />
                    <InputFormGroup
                        controlId="workflow-template-description-url-input"
                        label={resource.inputs.descriptionURL.label}
                        ariaLabel={resource.inputs.descriptionURL.label}
                        automationId="workflow-template-description-url-input"
                        placeholder={resource.inputs.descriptionURL.placeholder}
                        value={state.workflowTemplate.descriptionURL}
                        onChange={handleUpdateDescriptionURL}
                        disabled={state.requestInProgress}
                        isRequired={true}
                        labelWidth={labelWidth}
                    />
                    <OrganizationSelectFormGroup
                        idPrefix="management-console"
                        onChange={handleUpdateOrganizationOption}
                        axnCompanies={axnCompanies}
                        selectedOption={state.selectedOrganizationOption}
                        disabled={state.requestInProgress}
                        isRequired={false}
                        isSearchable={true}
                        isClearable={true}
                        labelWidth={labelWidth}
                    />
                    <TextareaFormGroup
                        controlId="workflow-template-template-input"
                        label={resource.inputs.template.label}
                        ariaLabel={resource.inputs.template.label}
                        automationId="workflow-template-template-input"
                        placeholder={resource.inputs.templateName.placeholder}
                        value={state.workflowTemplate.template}
                        onChange={handleUpdateTemplate}
                        disabled={state.requestInProgress}
                        isRequired={true}
                        labelWidth={labelWidth}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button id="add-workflow-template-cancel-button" variant="secondary" onClick={onModalClose}>{resources.common.buttons.cancel.label}</Button>
                    <ButtonWithSpinner
                        id="add-workflow-template-save-button"
                        disabled={state.requestInProgress}
                        hideSpinner={false}
                        variant="primary"
                        type={ButtonTypes.SUBMIT}
                        label={resources.common.buttons.save.label}
                    />
                </Modal.Footer>
            </Form>
        </DefaultModal>
    );
};